import { OTPInput, OTPInputContext } from "input-otp"
import { MinusIcon } from "lucide-react"
import * as React from "react"

import { cn } from "@/lib/utils"

const InputOTPErrorContext = React.createContext(false)

function InputOTP({
  className,
  containerClassName,
  hasError = false,
  ...props
}: React.ComponentProps<typeof OTPInput> & {
  containerClassName?: string
  hasError?: boolean
}) {
  return (
    <InputOTPErrorContext.Provider value={hasError}>
      <OTPInput
        data-slot="input-otp"
        containerClassName={cn(
          "flex items-center gap-2 has-disabled:opacity-50",
          containerClassName
        )}
        className={cn("disabled:cursor-not-allowed", className)}
        {...props}
      />
    </InputOTPErrorContext.Provider>
  )
}

function InputOTPGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="input-otp-group"
      className={cn("flex items-center", className)}
      {...props}
    />
  )
}

function InputOTPSlot({
  index,
  className,
  ...props
}: React.ComponentProps<"div"> & {
  index: number
}) {
  const inputOTPContext = React.useContext(OTPInputContext)
  const hasError = React.useContext(InputOTPErrorContext)
  const { char, hasFakeCaret, isActive } = inputOTPContext?.slots[index] ?? {}
  const isEmptyError = hasError && !char

  return (
    <div
      data-slot="input-otp-slot"
      data-active={isActive}
      className={cn(
        "bg-transparent relative flex h-14 w-9 items-center justify-center rounded-none border border-input-border text-sm transition-all outline-none first:rounded-tl-[var(--radius-input)] first:rounded-bl-[var(--radius-input)] last:rounded-tr-[var(--radius-input)] last:rounded-br-[var(--radius-input)] data-[active=true]:z-10 data-[active=true]:ring-1 data-[active=true]:ring-[var(--color-base-focus)] data-[active=true]:border-[var(--color-base-focus)]",
        isEmptyError && "border-destructive",
        className
      )}
      {...props}
    >
      {char}
      {hasFakeCaret && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="h-4 w-px animate-caret-blink bg-foreground duration-1000" />
        </div>
      )}
    </div>
  )
}

function InputOTPSeparator({ ...props }: React.ComponentProps<"div">) {
  return (
    <div data-slot="input-otp-separator" role="separator" {...props}>
      <MinusIcon />
    </div>
  )
}

export { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot }

